// This file contains helper functions for text color functionality
// The main text color logic is now in ColorCommand.js

import { $getSelection, $isRangeSelection } from 'lexical';
import { $isColorNode } from './ColorNode';

/**
 * Check if the current selection contains any colored text
 */
export function hasColorInSelection() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  const nodes = selection.getNodes();
  return nodes.some($isColorNode);
}

/**
 * Get all text colors in the current selection
 */
export function getColorsInSelection() {
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) {
    return [];
  }

  const nodes = selection.getNodes();
  const colorNodes = nodes.filter($isColorNode);

  const colors = colorNodes.map((node) => {
    const color = node.getColor();
    return color;
  });

  return colors;
}

/**
 * Check if the current selection has a specific text color
 */
export function hasSpecificColorInSelection(color) {
  const colors = getColorsInSelection();
  return colors.includes(color);
}

/**
 * Get the most common text color in the current selection
 */
export function getMostCommonColorInSelection() {
  const colors = getColorsInSelection();

  if (colors.length === 0) {
    return null;
  }

  // Count occurrences of each color
  const counts = {};
  colors.forEach((color) => {
    counts[color] = (counts[color] || 0) + 1;
  });

  // Find the most common one
  const result = Object.keys(counts).reduce((a, b) => (counts[a] > counts[b] ? a : b));
  return result;
}

/**
 * Get text color from DOM styles (fallback method)
 */
export function getColorFromDOM() {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const container = range.commonAncestorContainer;

  // Walk up the DOM tree to find the first element with color style
  let element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;

  while (element && element !== document.body) {
    const computedStyle = window.getComputedStyle(element);
    const { color } = computedStyle;

    if (
      color &&
      color !== 'inherit' &&
      color !== 'initial' &&
      color !== 'rgb(0, 0, 0)' &&
      color !== 'black'
    ) {
      return color;
    }

    element = element.parentElement;
  }

  return null;
}
